class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.predictiveSearchResultsContent =
      this.predictiveSearchResults?.querySelector('[data-predictive-search-content]') ||
      this.predictiveSearchResults;
    this.isHeaderSearch = this.closest('site-search') !== null;
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.activeFetchQuery = '';

    this.setupEventListeners();
    this.setupResultsPortal();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  setupResultsPortal() {
    if (!this.isHeaderSearch || !this.predictiveSearchResults || this.predictiveSearchResults.parentElement === document.body) return;

    this.predictiveSearchResults.classList.add('site-search__results--portal');
    document.body.appendChild(this.predictiveSearchResults);
  }

  getResultsRoot() {
    return this.predictiveSearchResults || this;
  }

  getResultElement(selector) {
    return this.querySelector(selector) || this.getResultsRoot().querySelector(selector);
  }

  getResultElements(selector) {
    return [...new Set([
      ...this.querySelectorAll(selector),
      ...this.getResultsRoot().querySelectorAll(selector),
    ])];
  }

  getSelectedOption() {
    return this.getResultElement('[aria-selected="true"]');
  }

  getSelectedAction() {
    const selectedOption = this.getSelectedOption();

    if (!selectedOption) return null;
    if (selectedOption.matches('a, button')) return selectedOption;

    return selectedOption.querySelector('a, button');
  }

  getVisibleResultOptions() {
    return this.getResultElements('[role="option"]').filter((element) => element.offsetParent !== null);
  }

  isFocusInsideSearch() {
    const activeElement = document.activeElement;

    return this.contains(activeElement) || this.getResultsRoot().contains(activeElement);
  }

  setResultsPanelState(state, isActive) {
    this.predictiveSearchResults?.classList.toggle(state, isActive);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.predictiveSearchResultsContent
        ?.querySelector("#predictive-search-results-groups-wrapper")
        ?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.getSelectedAction()) event.preventDefault();
  }

  shouldResetForm() {
    return !this.getSelectedAction();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.isFocusInsideSearch()) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    ) || this.getResultsRoot().querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const previousTermMatches = currentButtonText.match(new RegExp(escapedPreviousTerm, "g")) || [];

      if (previousTermMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.getSelectedOption();

    const allVisibleElements = this.getVisibleResultOptions();
    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.getSelectedAction();

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    this.activeFetchQuery = searchTerm;

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.activeFetchQuery !== this.searchTerm) return;

        const parsedResults = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!parsedResults) {
          this.close();
          return;
        }

        const resultsMarkup = parsedResults.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Code 20 and AbortError both mean the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.setResultsPanelState('is-loading', true);
    document.body.classList.add('search-predictive-open');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResultsContent.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setResultsPanelState('is-loading', false);
    const liveRegionCount = this.getResultElement('[data-predictive-search-live-region-count-value]');

    if (liveRegionCount) this.setLiveRegionText(liveRegionCount.textContent);
  }

  getResultsMaxHeight() {
    const searchPanel = this.closest('.site-search__panel');
    const searchBar = searchPanel?.querySelector('.site-search__bar');
    const panelTop = searchBar
      ? searchBar.getBoundingClientRect().bottom
      : searchPanel
      ? searchPanel.getBoundingClientRect().bottom
      : document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;

    this.predictiveSearchResults.style.setProperty('--predictive-search-panel-top', `${panelTop}px`);
    this.resultsMaxHeight = window.innerHeight - panelTop;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.setResultsPanelState('is-open', true);
    this.input.setAttribute('aria-expanded', true);
    document.body.classList.add('search-predictive-open');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.searchTerm = '';
      this.predictiveSearchResultsContent.innerHTML = '';
    }
    const selected = this.getSelectedOption();

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.setResultsPanelState('is-open', false);
    this.setResultsPanelState('is-loading', false);
    this.input.setAttribute('aria-expanded', false);
    if (!document.querySelector('predictive-search[open], predictive-search[loading]')) {
      document.body.classList.remove('search-predictive-open');
    }
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
